import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Checkbox,
  Card,
  message,
  Input,
  Button,
  InputNumber,
  Switch,
  Space,
  Tabs,
  Tag,
  Modal,
  Progress,
  Alert,
  Statistic,
  Row,
  Col,
  Popconfirm,
  Select,
} from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { isResumenCuentaEnabledForCoop } from "../../../utils/resumenCuentaAccess.js";

const filterRows = (data, { numero = "", cuenta = "", nombre = "" }) => {
  const numeroFiltro = String(numero || "").toLowerCase();
  const cuentaFiltro = String(cuenta || "").toLowerCase();
  const nombreFiltro = String(nombre || "").toLowerCase();

  return data.filter((item) => {
    const itemNumero = String(item.numero || "").toLowerCase();
    const itemCuenta = String(item.cuenta || "").toLowerCase();
    const itemNombre = String(item.nombre || "").toLowerCase();

    return (
      (!numeroFiltro || itemNumero.includes(numeroFiltro)) &&
      (!cuentaFiltro || itemCuenta.includes(cuentaFiltro)) &&
      (!nombreFiltro || itemNombre.includes(nombreFiltro))
    );
  });
};

const estadoCampaniaTag = (estado) => {
  const map = {
    en_curso: { color: "processing", label: "En curso" },
    completado: { color: "success", label: "Completado" },
    pausado_spam: { color: "error", label: "Pausado (spam Meta)" },
    error: { color: "error", label: "Error" },
  };
  const conf = map[estado] || { color: "default", label: estado || "—" };
  return <Tag color={conf.color}>{conf.label}</Tag>;
};

const estadoDestTag = (estado) => {
  const map = {
    pendiente: { color: "default", label: "Pendiente" },
    aceptado: { color: "blue", label: "Aceptado Meta" },
    enviado: { color: "cyan", label: "Enviado" },
    entregado: { color: "green", label: "Entregado" },
    leido: { color: "purple", label: "Leído" },
    fallido: { color: "error", label: "Fallido" },
    error_pdf: { color: "volcano", label: "Error PDF" },
    omitido_rate_limit: { color: "orange", label: "Omitido (rate limit)" },
  };
  const conf = map[estado] || { color: "default", label: estado || "—" };
  return <Tag color={conf.color}>{conf.label}</Tag>;
};

const CDifundirResumenCuenta = () => {
  const [activeTab, setActiveTab] = useState("config");
  const [mensajes, setMensajes] = useState([]);
  const [filteredMensajes, setFilteredMensajes] = useState([]);
  const [selectedNumeros, setSelectedNumeros] = useState([]);
  const [filteredSelectedNumeros, setFilteredSelectedNumeros] = useState([]);
  const [diaEnvio, setDiaEnvio] = useState(1);
  const [horaEnvio, setHoraEnvio] = useState("09:00");
  const [enviarMesAnterior, setEnviarMesAnterior] = useState(false);

  const [searchNumero, setSearchNumero] = useState("");
  const [searchCuenta, setSearchCuenta] = useState("");
  const [searchNombre, setSearchNombre] = useState("");

  const [selectedSearchNumero, setSelectedSearchNumero] = useState("");
  const [selectedSearchCuenta, setSelectedSearchCuenta] = useState("");
  const [selectedSearchNombre, setSelectedSearchNombre] = useState("");

  const [mensajesPagination, setMensajesPagination] = useState({
    current: 1,
    pageSize: 5,
  });
  const [selectedPagination, setSelectedPagination] = useState({
    current: 1,
    pageSize: 5,
  });

  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [activo, setActivo] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleFiltro, setDetalleFiltro] = useState("");
  const [detalleEstadoFiltro, setDetalleEstadoFiltro] = useState("todos");
  const [iniciandoEnvio, setIniciandoEnvio] = useState(false);
  const [accionCampaniaId, setAccionCampaniaId] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const empresaId = user?.user?.idCooperativa;
  const token = user?.token;
  const api = process.env.REACT_APP_API_URL;
  const isEnabledForCoop = isResumenCuentaEnabledForCoop(empresaId);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const loadHistorial = useCallback(async () => {
    if (!empresaId) return;
    setHistorialLoading(true);
    try {
      const [histRes, activoRes] = await Promise.all([
        axios.get(`${api}/difundir-resumen-cuenta/${empresaId}/historial`, {
          headers: authHeaders,
        }),
        axios.get(`${api}/difundir-resumen-cuenta/${empresaId}/activo`, {
          headers: authHeaders,
        }),
      ]);
      setHistorial(histRes.data || []);
      setActivo(activoRes.data || null);
    } catch (error) {
      console.error(error);
      message.error("No se pudo cargar el historial de envíos.");
    } finally {
      setHistorialLoading(false);
    }
  }, [api, authHeaders, empresaId]);

  useEffect(() => {
    if (!isEnabledForCoop) return;

    const fetchData = async () => {
      try {
        const mensajesResponse = await axios.get(`${api}/mensajes/${empresaId}`, {
          headers: authHeaders,
        });

        const datosTransformados = mensajesResponse.data.flatMap((mensaje) =>
          mensaje.datosCuenta.map((cuenta) => ({
            key: `${mensaje.numero}-${cuenta.cuenta}`,
            numero: String(mensaje.numero || "").trim(),
            cuenta: String(cuenta.cuenta || "").trim(),
            nombre: cuenta.nombre || "",
          }))
        );

        setMensajes(datosTransformados);
        setFilteredMensajes(datosTransformados);

        const configuracionResponse = await axios.get(
          `${api}/difundir-resumen-cuenta/${empresaId}`,
          { headers: authHeaders }
        );

        if (configuracionResponse.data?.telefonos) {
          const telefonos = configuracionResponse.data.telefonos.map((tel) => ({
            key: `${tel.numero}-${tel.cuenta}`,
            numero: String(tel.numero || "").trim(),
            cuenta: String(tel.cuenta || "").trim(),
            nombre: tel.nombre || "",
          }));
          setSelectedNumeros(telefonos);
          setFilteredSelectedNumeros(telefonos);
        }

        if (configuracionResponse.data?.diaEnvio) {
          setDiaEnvio(configuracionResponse.data.diaEnvio);
        }

        if (configuracionResponse.data?.horaEnvio) {
          setHoraEnvio(configuracionResponse.data.horaEnvio);
        }

        setEnviarMesAnterior(Boolean(configuracionResponse.data?.enviarMesAnterior));
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404) {
          console.error("Error al cargar datos:", error);
          message.error("Error al cargar los datos.");
        }
      }
    };

    fetchData();
    loadHistorial();
  }, [api, authHeaders, empresaId, isEnabledForCoop, loadHistorial]);

  useEffect(() => {
    if (activeTab !== "historial" || !isEnabledForCoop) return undefined;
    const hasActive = Boolean(activo) || historial.some((h) => h.estado === "en_curso");
    if (!hasActive) return undefined;
    const timer = setInterval(() => {
      loadHistorial();
    }, 8000);
    return () => clearInterval(timer);
  }, [activeTab, activo, historial, isEnabledForCoop, loadHistorial]);

  useEffect(() => {
    if (!detalleOpen || !detalle?._id) return undefined;
    if (detalle.estado !== "en_curso") return undefined;
    const timer = setInterval(() => {
      openDetalle(detalle._id);
    }, 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleOpen, detalle?._id, detalle?.estado]);

  const isRecordSelected = (record) =>
    selectedNumeros.some(
      (item) =>
        String(item.numero).trim() === String(record.numero).trim() &&
        String(item.cuenta).trim() === String(record.cuenta).trim()
    );

  useEffect(() => {
    setFilteredMensajes(
      filterRows(mensajes, {
        numero: searchNumero,
        cuenta: searchCuenta,
        nombre: searchNombre,
      })
    );
  }, [mensajes, searchCuenta, searchNombre, searchNumero]);

  useEffect(() => {
    setFilteredSelectedNumeros(
      filterRows(selectedNumeros, {
        numero: selectedSearchNumero,
        cuenta: selectedSearchCuenta,
        nombre: selectedSearchNombre,
      })
    );
  }, [
    selectedNumeros,
    selectedSearchCuenta,
    selectedSearchNombre,
    selectedSearchNumero,
  ]);

  const handleCheckboxChange = (record) => {
    const isSelected = isRecordSelected(record);

    if (isSelected) {
      const updated = selectedNumeros.filter(
        (item) =>
          !(
            String(item.numero).trim() === String(record.numero).trim() &&
            String(item.cuenta).trim() === String(record.cuenta).trim()
          )
      );
      setSelectedNumeros(updated);
      return;
    }

    setSelectedNumeros([...selectedNumeros, { ...record }]);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      const updated = [...selectedNumeros];
      filteredMensajes.forEach((record) => {
        if (!isRecordSelected(record)) {
          updated.push(record);
        }
      });
      setSelectedNumeros(updated);
      return;
    }

    const updated = selectedNumeros.filter(
      (item) =>
        !filteredMensajes.some(
          (record) => record.numero === item.numero && record.cuenta === item.cuenta
        )
    );
    setSelectedNumeros(updated);
  };

  const handleRemoveNumero = (numero, cuenta) => {
    const updated = selectedNumeros.filter(
      (item) =>
        !(
          String(item.numero).trim() === String(numero).trim() &&
          String(item.cuenta).trim() === String(cuenta).trim()
        )
    );
    setSelectedNumeros(updated);
  };

  const handleGuardarConfiguracion = async () => {
    if (selectedNumeros.length === 0) {
      message.error("Debe seleccionar al menos un número.");
      return;
    }

    if (!Number.isInteger(diaEnvio) || diaEnvio < 1 || diaEnvio > 31) {
      message.error("El día de envío debe ser entre 1 y 31.");
      return;
    }

    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(horaEnvio)) {
      message.error("La hora debe tener formato HH:mm.");
      return;
    }

    try {
      const telefonosData = selectedNumeros.map(({ numero, cuenta, nombre }) => ({
        numero,
        cuenta,
        nombre,
      }));

      await axios.post(
        `${api}/difundir-resumen-cuenta`,
        {
          idCooperativa: empresaId,
          telefonos: telefonosData,
          diaEnvio,
          horaEnvio,
          enviarMesAnterior,
        },
        {
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("Configuración guardada correctamente.");
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      message.error("Error al guardar la configuración.");
    }
  };

  const handleProbarAhora = async () => {
    setIniciandoEnvio(true);
    try {
      const response = await axios.post(
        `${api}/difundir-resumen-cuenta/probar`,
        { idCooperativa: empresaId },
        {
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
        }
      );

      message.success(
        response?.data?.message ||
          "Envío iniciado. Podés seguir el progreso en el historial."
      );
      setActiveTab("historial");
      await loadHistorial();
      if (response?.data?.campaignId) {
        openDetalle(response.data.campaignId);
      }
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message || "Error al iniciar el envío.";
      message.error(serverMessage);
    } finally {
      setIniciandoEnvio(false);
    }
  };

  const openDetalle = async (campaignId) => {
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalle(null);
    setDetalleFiltro("");
    setDetalleEstadoFiltro("todos");
    try {
      const res = await axios.get(
        `${api}/difundir-resumen-cuenta/${empresaId}/historial/${campaignId}`,
        { headers: authHeaders }
      );
      setDetalle(res.data);
    } catch (error) {
      console.error(error);
      message.error("No se pudo cargar el detalle de la campaña.");
      setDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const canResumeCampaign = (row) => {
    if (!row || row.estado === "en_curso") return false;
    const c = row.counts || {};
    return (c.pendiente || 0) + (c.omitido_rate_limit || 0) > 0;
  };

  const canRetryErrorsCampaign = (row) => {
    if (!row || row.estado === "en_curso") return false;
    return countRetryableErrors(row) > 0;
  };

  /** Fallidos reintentables: excluye Meta #131026 (undeliverable). */
  const countUndeliverableErrors = (row) => {
    const dest = row?.destinatarios;
    if (!Array.isArray(dest) || !dest.length) return 0;
    return dest.filter((d) => String(d?.errorCode || "") === "131026").length;
  };

  const countRetryableErrors = (row) => {
    const dest = row?.destinatarios;
    if (Array.isArray(dest) && dest.length) {
      return dest.filter((d) => {
        if (String(d?.errorCode || "") === "131026") return false;
        return (
          d.estado === "fallido" ||
          d.estado === "error_pdf" ||
          d.estado === "omitido_rate_limit"
        );
      }).length;
    }
    const c = row?.counts || {};
    return (c.fallido || 0) + (c.error_pdf || 0) + (c.omitido_rate_limit || 0);
  };

  const getReenviarErroresDescription = (row) => {
    const retryable = countRetryableErrors(row);
    const undeliverable = countUndeliverableErrors(row);
    const undeliverableMsg =
      undeliverable > 0
        ? ` Se excluirán ${undeliverable} undeliverable (#131026): WhatsApp no puede entregar a esos números.`
        : " Los undeliverable (#131026) se excluyen automáticamente.";
    return `Se reenviará a ${retryable} destinatario(s) con error reintentable.${undeliverableMsg} ¿Continuar?`;
  };

  const handleReanudar = async (campaignId) => {
    setAccionCampaniaId(campaignId);
    try {
      const res = await axios.post(
        `${api}/difundir-resumen-cuenta/${empresaId}/historial/${campaignId}/reanudar`,
        {},
        { headers: authHeaders }
      );
      message.success(res?.data?.message || "Reanudación iniciada.");
      setActiveTab("historial");
      await loadHistorial();
      openDetalle(campaignId);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "No se pudo reanudar el envío."
      );
    } finally {
      setAccionCampaniaId(null);
    }
  };

  const handleReenviarErrores = async (campaignId) => {
    setAccionCampaniaId(campaignId);
    try {
      const res = await axios.post(
        `${api}/difundir-resumen-cuenta/${empresaId}/historial/${campaignId}/reenviar-errores`,
        {},
        { headers: authHeaders }
      );
      message.success(res?.data?.message || "Reenvío a erróneos iniciado.");
      setActiveTab("historial");
      await loadHistorial();
      openDetalle(campaignId);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "No se pudo reenviar a los erróneos."
      );
    } finally {
      setAccionCampaniaId(null);
    }
  };

  const areAllSelected =
    filteredMensajes.length > 0 &&
    filteredMensajes.every((record) => isRecordSelected(record));

  const buildTablePagination = (paginationState, setPaginationState) => ({
    current: paginationState.current,
    pageSize: paginationState.pageSize,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
    showTotal: (total) => `${total} registros`,
    onChange: (page, pageSize) => {
      setPaginationState({ current: page, pageSize });
    },
    onShowSizeChange: (_current, pageSize) => {
      setPaginationState({ current: 1, pageSize });
    },
  });

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={isRecordSelected(record)}
          onChange={() => handleCheckboxChange(record)}
        />
      ),
    },
    { title: "Número de Teléfono", dataIndex: "numero", key: "numero" },
    { title: "Número de Cuenta", dataIndex: "cuenta", key: "cuenta" },
    { title: "Nombre de Cuenta", dataIndex: "nombre", key: "nombre" },
  ];

  const selectedColumns = [
    { title: "Número de Teléfono", dataIndex: "numero", key: "numero" },
    { title: "Número de Cuenta", dataIndex: "cuenta", key: "cuenta" },
    { title: "Nombre de Cuenta", dataIndex: "nombre", key: "nombre" },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          danger
          onClick={() => handleRemoveNumero(record.numero, record.cuenta)}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  const historialColumns = [
    {
      title: "Inicio",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (d) => (d ? new Date(d).toLocaleString("es-AR") : "—"),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      render: (t) => (t === "manual" ? "Manual" : "Programado"),
    },
    {
      title: "Período",
      dataIndex: "periodo",
      key: "periodo",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (e) => estadoCampaniaTag(e),
    },
    { title: "Total", dataIndex: "total", key: "total", width: 70 },
    {
      title: "Aceptados Meta",
      dataIndex: "aceptadosMeta",
      key: "aceptadosMeta",
      width: 120,
    },
    {
      title: "Entregados",
      dataIndex: "entregados",
      key: "entregados",
      width: 100,
    },
    {
      title: "Leídos",
      dataIndex: "leidos",
      key: "leidos",
      width: 80,
    },
    {
      title: "Errores",
      dataIndex: "errores",
      key: "errores",
      width: 90,
    },
    {
      title: "Acciones",
      key: "go",
      width: 280,
      render: (_, row) => (
        <Space size={0} wrap>
          <Button type="link" onClick={() => openDetalle(row._id)}>
            Ver detalle
          </Button>
          {canResumeCampaign(row) && (
            <Popconfirm
              title="Reanudar envío"
              description="Continúa con pendientes/omitidos por rate limit. ¿Continuar?"
              okText="Reanudar"
              cancelText="Cancelar"
              onConfirm={() => handleReanudar(row._id)}
            >
              <Button type="link" loading={accionCampaniaId === row._id}>
                Reanudar
              </Button>
            </Popconfirm>
          )}
          {canRetryErrorsCampaign(row) && (
            <Popconfirm
              title="Reenviar a erróneos"
              description={getReenviarErroresDescription(row)}
              okText="Reenviar"
              cancelText="Cancelar"
              onConfirm={() => handleReenviarErrores(row._id)}
            >
              <Button type="link" loading={accionCampaniaId === row._id}>
                Reenviar errores
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const destinatariosFiltrados = useMemo(() => {
    const rows = detalle?.destinatarios || [];
    const q = detalleFiltro.trim().toLowerCase();
    const ERROR_STATES = new Set(["fallido", "error_pdf", "omitido_rate_limit"]);

    return rows.filter((d) => {
      if (detalleEstadoFiltro === "errores") {
        if (!ERROR_STATES.has(d.estado)) return false;
      } else if (detalleEstadoFiltro !== "todos") {
        if (d.estado !== detalleEstadoFiltro) return false;
      }

      if (!q) return true;
      return (
        String(d.numero || "")
          .toLowerCase()
          .includes(q) ||
        String(d.cuenta || "")
          .toLowerCase()
          .includes(q) ||
        String(d.nombre || "")
          .toLowerCase()
          .includes(q) ||
        String(d.estado || "")
          .toLowerCase()
          .includes(q) ||
        String(d.errorCode || "")
          .toLowerCase()
          .includes(q) ||
        String(d.errorMessage || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [detalle, detalleFiltro, detalleEstadoFiltro]);

  const progresoPct = (row) => {
    if (!row?.total) return 0;
    const done =
      (row.aceptadosMeta || 0) +
      (row.counts?.fallido || 0) +
      (row.counts?.error_pdf || 0) +
      (row.counts?.omitido_rate_limit || 0);
    return Math.min(100, Math.round((done / row.total) * 100));
  };

  const configTab = (
    <>
      <div
        style={{
          display: "flex",
          width: "100%",
          gap: "16px",
          marginTop: "16px",
          marginBottom: "16px",
          alignItems: "center",
        }}
      >
        <Input
          value={searchNumero}
          onChange={(e) => {
            setSearchNumero(e.target.value);
            setMensajesPagination((prev) => ({ ...prev, current: 1 }));
          }}
          placeholder="Buscar por número de teléfono"
          prefix={<SearchOutlined />}
          style={{ flex: "1 1 0", minWidth: "0" }}
        />
        <Input
          value={searchCuenta}
          onChange={(e) => {
            setSearchCuenta(e.target.value);
            setMensajesPagination((prev) => ({ ...prev, current: 1 }));
          }}
          placeholder="Buscar por cuenta"
          prefix={<SearchOutlined />}
          style={{ flex: "1 1 0", minWidth: "0" }}
        />
        <Input
          value={searchNombre}
          onChange={(e) => {
            setSearchNombre(e.target.value);
            setMensajesPagination((prev) => ({ ...prev, current: 1 }));
          }}
          placeholder="Buscar por nombre"
          prefix={<SearchOutlined />}
          style={{ flex: "1 1 0", minWidth: "0" }}
        />
        <Button
          onClick={() => {
            setSearchNumero("");
            setSearchCuenta("");
            setSearchNombre("");
            setMensajesPagination((prev) => ({ ...prev, current: 1 }));
          }}
          style={{ marginLeft: "auto" }}
        >
          Limpiar filtros
        </Button>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <Checkbox checked={areAllSelected} onChange={(e) => handleSelectAll(e.target.checked)}>
          {areAllSelected ? "Deseleccionar todos" : "Seleccionar todos los filtrados"}
        </Checkbox>
      </div>

      <Table
        dataSource={filteredMensajes}
        columns={columns}
        rowKey={(record) => `${record.numero}-${record.cuenta}`}
        pagination={buildTablePagination(mensajesPagination, setMensajesPagination)}
      />

      <Card title="Números seleccionados para envío automático" style={{ marginTop: "16px" }}>
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: "16px",
            marginTop: "16px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <Input
            value={selectedSearchNumero}
            onChange={(e) => {
              setSelectedSearchNumero(e.target.value);
              setSelectedPagination((prev) => ({ ...prev, current: 1 }));
            }}
            placeholder="Buscar por número de teléfono"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: 0 }}
          />
          <Input
            value={selectedSearchCuenta}
            onChange={(e) => {
              setSelectedSearchCuenta(e.target.value);
              setSelectedPagination((prev) => ({ ...prev, current: 1 }));
            }}
            placeholder="Buscar por cuenta"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: 0 }}
          />
          <Input
            value={selectedSearchNombre}
            onChange={(e) => {
              setSelectedSearchNombre(e.target.value);
              setSelectedPagination((prev) => ({ ...prev, current: 1 }));
            }}
            placeholder="Buscar por nombre"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: 0 }}
          />
          <Button
            onClick={() => {
              setSelectedSearchNumero("");
              setSelectedSearchCuenta("");
              setSelectedSearchNombre("");
              setSelectedPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ marginLeft: "auto" }}
          >
            Limpiar filtros
          </Button>
        </div>

        <Table
          dataSource={filteredSelectedNumeros}
          columns={selectedColumns}
          rowKey={(record) => `${record.numero}-${record.cuenta}`}
          pagination={buildTablePagination(selectedPagination, setSelectedPagination)}
        />
      </Card>

      <Card title="Programación mensual" style={{ marginTop: "16px" }}>
        <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <span style={{ marginRight: "8px" }}>Día del mes:</span>
            <InputNumber
              min={1}
              max={31}
              value={diaEnvio}
              onChange={(value) => setDiaEnvio(value)}
            />
          </div>
          <div>
            <span style={{ marginRight: "8px" }}>Hora:</span>
            <Input
              type="time"
              value={horaEnvio}
              onChange={(e) => setHoraEnvio(e.target.value)}
              style={{ width: "130px" }}
            />
          </div>
          <Space align="center" wrap style={{ marginTop: "4px" }}>
            <span>Período del PDF:</span>
            <span
              style={{
                fontWeight: enviarMesAnterior ? 400 : 600,
                color: enviarMesAnterior ? undefined : "#1677ff",
              }}
            >
              Mes actual
            </span>
            <Switch checked={enviarMesAnterior} onChange={setEnviarMesAnterior} />
            <span
              style={{
                fontWeight: enviarMesAnterior ? 600 : 400,
                color: enviarMesAnterior ? "#1677ff" : undefined,
              }}
            >
              Mes anterior
            </span>
          </Space>
        </div>
      </Card>

      <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
        <Button type="primary" onClick={handleGuardarConfiguracion}>
          Guardar configuración
        </Button>
        <Button onClick={handleProbarAhora} loading={iniciandoEnvio}>
          Enviar ahora
        </Button>
      </div>
    </>
  );

  const historialTab = (
    <>
      {activo && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`Envío en curso · período ${activo.periodo || "—"}`}
          description={
            <div>
              <Progress percent={progresoPct(activo)} status="active" />
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col>
                  <Statistic title="Total" value={activo.total || 0} />
                </Col>
                <Col>
                  <Statistic title="Aceptados Meta" value={activo.aceptadosMeta || 0} />
                </Col>
                <Col>
                  <Statistic title="Entregados" value={activo.entregados || 0} />
                </Col>
                <Col>
                  <Statistic title="Leídos" value={activo.leidos || 0} />
                </Col>
                <Col>
                  <Statistic title="Errores" value={activo.errores || 0} />
                </Col>
              </Row>
              <Button
                type="link"
                style={{ paddingLeft: 0, marginTop: 8 }}
                onClick={() => openDetalle(activo._id)}
              >
                Ver detalle en vivo
              </Button>
            </div>
          }
        />
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <Button icon={<ReloadOutlined />} onClick={loadHistorial} loading={historialLoading}>
          Actualizar historial
        </Button>
      </div>

      <Table
        rowKey={(r) => r._id}
        loading={historialLoading}
        dataSource={historial}
        columns={historialColumns}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />
    </>
  );

  if (!isEnabledForCoop) {
    return (
      <div className="p-4">
        <Card title="Resumen de cuenta automático">
          <p>Esta funcionalidad no está habilitada para esta cooperativa.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Card title="Resumen de cuenta automático" className="mb-4">
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Envíos masivos y Meta #131048"
          description="Si WhatsApp marca Spam Rate limit hit, el bot frena el lote automáticamente para proteger la calidad del número. El historial muestra a quién se envió, quién lo recibió/leyó y los errores."
        />

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            if (key === "historial") loadHistorial();
          }}
          items={[
            { key: "config", label: "Configuración", children: configTab },
            { key: "historial", label: "Historial y progreso", children: historialTab },
          ]}
        />
      </Card>

      <Modal
        open={detalleOpen}
        onCancel={() => setDetalleOpen(false)}
        footer={null}
        width={1100}
        title={
          detalle
            ? `Detalle · ${detalle.periodo || "—"} · ${estadoCampaniaTag(detalle.estado)}`
            : "Detalle de envío"
        }
      >
        {detalleLoading && <p>Cargando…</p>}
        {!detalleLoading && detalle && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} md={4}>
                <Statistic title="Total" value={detalle.total || 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Aceptados Meta" value={detalle.aceptadosMeta || 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Entregados" value={detalle.entregados || 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Leídos" value={detalle.leidos || 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Errores" value={detalle.errores || 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic
                  title="Pendientes"
                  value={detalle.counts?.pendiente || 0}
                />
              </Col>
            </Row>

            {detalle.estado === "pausado_spam" && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Campaña pausada por Meta #131048 (Spam Rate limit hit)"
                description="Meta restringió el envío por calidad/spam del número. Podés reanudar pendientes/omitidos o reenviar a los erróneos reintentables (se excluyen undeliverable #131026)."
              />
            )}

            <Space wrap style={{ marginBottom: 12 }}>
              {canResumeCampaign(detalle) && (
                <Popconfirm
                  title="Reanudar envío"
                  description="Continúa con pendientes/omitidos por rate limit."
                  okText="Reanudar"
                  cancelText="Cancelar"
                  onConfirm={() => handleReanudar(detalle._id)}
                >
                  <Button type="primary" loading={accionCampaniaId === detalle._id}>
                    Reanudar envío
                  </Button>
                </Popconfirm>
              )}
              {canRetryErrorsCampaign(detalle) && (
                <Popconfirm
                  title="Reenviar a erróneos"
                  description={getReenviarErroresDescription(detalle)}
                  okText="Reenviar"
                  cancelText="Cancelar"
                  onConfirm={() => handleReenviarErrores(detalle._id)}
                >
                  <Button loading={accionCampaniaId === detalle._id}>
                    Reenviar a erróneos
                  </Button>
                </Popconfirm>
              )}
            </Space>

            <Space wrap style={{ marginBottom: 12, width: "100%" }} align="start">
              <Select
                value={detalleEstadoFiltro}
                onChange={setDetalleEstadoFiltro}
                style={{ width: 220 }}
                options={[
                  { value: "todos", label: "Todos los estados" },
                  {
                    value: "errores",
                    label: `Solo errores (${detalle.errores || 0})`,
                  },
                  { value: "fallido", label: "Fallido" },
                  { value: "error_pdf", label: "Error PDF" },
                  { value: "omitido_rate_limit", label: "Omitido rate limit" },
                  { value: "leido", label: "Leído" },
                  { value: "entregado", label: "Entregado" },
                  { value: "enviado", label: "Enviado" },
                  { value: "aceptado", label: "Aceptado Meta" },
                  { value: "pendiente", label: "Pendiente" },
                ]}
              />
              <Input
                placeholder="Filtrar por número, cuenta, nombre o error"
                value={detalleFiltro}
                onChange={(e) => setDetalleFiltro(e.target.value)}
                style={{ width: 360, maxWidth: "100%" }}
                prefix={<SearchOutlined />}
                allowClear
              />
              <span style={{ color: "#888", lineHeight: "32px" }}>
                Mostrando {destinatariosFiltrados.length} de{" "}
                {(detalle.destinatarios || []).length}
              </span>
            </Space>

            <Table
              size="small"
              rowKey={(r) => `${r.numero}-${r.cuenta}-${r.messageId || ""}`}
              dataSource={destinatariosFiltrados}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              columns={[
                { title: "Número", dataIndex: "numero", key: "numero", width: 150 },
                { title: "Cuenta", dataIndex: "cuenta", key: "cuenta", width: 90 },
                { title: "Nombre", dataIndex: "nombre", key: "nombre", ellipsis: true },
                {
                  title: "Estado",
                  dataIndex: "estado",
                  key: "estado",
                  width: 150,
                  render: (e) => estadoDestTag(e),
                },
                {
                  title: "Código error",
                  dataIndex: "errorCode",
                  key: "errorCode",
                  width: 110,
                  render: (v) => v || "—",
                },
                {
                  title: "Motivo",
                  dataIndex: "errorMessage",
                  key: "errorMessage",
                  ellipsis: true,
                  render: (v) => v || "—",
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default CDifundirResumenCuenta;
